import { IState } from "..";
import { createSlice } from "@reduxjs/toolkit";
import { initialAccountDetailsState } from "./AccountDetailsState";
import { AppThunk } from "../../app/store";
import AccountDetailsService from "../../app/data/account-details/accountDetailsService";
import CompanyService from "../../app/data/company/companyService";
import TrackingService from "../../app/data/tracking/trackingService";

const accountDetailsService = AccountDetailsService.getInstance();
const companyService = CompanyService.getInstance();
const trackingService = TrackingService.getInstance();

export const accountDetailsSlice = createSlice({
  name: "accountDetails",
  initialState: initialAccountDetailsState,
  reducers: {
    requestStarted: (state, { payload }) => {
      state.request[payload.requester] = {
        requestStarted: true,
        requestSucceed: false,
        requestFailed: false,
        requestError: null,
      };
    },
    requestSucceed: (state, { payload }) => {
      state.request[payload.requester] = {
        requestStarted: false,
        requestSucceed: true,
        requestFailed: false,
      };      
    },
    requestFailed: (state, { payload }) => {
      state.request[payload.requester] = {
        requestStarted: false,
        requestSucceed: false,
        requestFailed: true,
        requestError: payload.error,
      };
    },
    requestScroll: (state, { payload }) => {
      state.requestScroll[payload.requester] = payload.scroll;
    }, 
    setCompanyInfo: (state, { payload }) => {
      state.companyInfo = payload;
    },    
    setAccountDetailsSummary: (state, { payload }) => {
      state.companySummary = payload;
    },
    setAccountDetailsRevenue: (state, { payload }) => {
      state.revenue = payload.accountRevenue;
    },    
    setAccounts: (state, { payload }) => {
      state.accounts = payload.content;
    },
    addAccounts: (state, { payload }) => {
      state.accounts = [...state.accounts, ...payload.content];
    },
    setInvoices: (state, { payload }) => {
      state.invoices = payload;
    },
    addInvoices: (state, { payload }) => {
      state.invoices = [...state.invoices, ...payload];
    },
  }
});

export const {
  requestStarted,
  requestSucceed,
  requestFailed,
  requestScroll, 
  setCompanyInfo,
  setAccountDetailsSummary,
  setAccountDetailsRevenue,
  addAccounts,
  setAccounts,
  addInvoices,
  setInvoices,
} = accountDetailsSlice.actions;

export const getAccountDetailsCompanyInfo = (
  accountNumber: string
): AppThunk => async (dispatch) => {
  dispatch(requestStarted({ requester: "GET_COMPANY_INFO" }));
  const response = await accountDetailsService.getAccountDetailsCompanyInfo(accountNumber);
  if (response.isCancel?.()) return;
  if (response.ok()) {    
    dispatch(setCompanyInfo(response.data));
    dispatch(requestSucceed({ requester: "GET_COMPANY_INFO" }));
  } else {
    dispatch(requestFailed({ requester: "GET_COMPANY_INFO", error: response.getError ? response.getError() : "Error"}));
  }
};

export const getAccountDetailsCompanySummary = (
  accountNumber: string,
): AppThunk => async (dispatch) => {
  dispatch(requestStarted({ requester: "GET_COMPANY_SUMMARY"}));
  const response = await accountDetailsService.getAccountDetailsCompanySummary(accountNumber);
  if (response.isCancel?.()) return;
  if (response.ok()) {    
    dispatch(setAccountDetailsSummary(response.data));
    dispatch(requestSucceed({ requester: "GET_COMPANY_SUMMARY"}));
  } else {
    dispatch(requestFailed({ requester: "GET_COMPANY_SUMMARY", error: response.getError ? response.getError() : "Error" }));
  }
};

export const getAccountDetailsRevenue = (
  accountNumber: string,
): AppThunk => async (dispatch) => {
  dispatch(requestStarted({ requester: "GET_REVENUE"}));
  const response = await accountDetailsService.getAccountDetailsRevenue(accountNumber);
  if (response.isCancel?.()) return;
  if (response.ok()) {    
    dispatch(setAccountDetailsRevenue(response.data));
    dispatch(requestSucceed({ requester: "GET_REVENUE"}));
  } else {
    dispatch(requestFailed({ requester: "GET_REVENUE", error: response.getError ? response.getError() : "Error" }));
  }
};

export const getAccountDetailsAccounts = (
  teamId: string,
  paypointNumber: string | null,
  lastIds?: string
): AppThunk => async (dispatch) => {
  dispatch(requestStarted({ requester: lastIds ? "GET_ACCOUNTS_PORTION" : "GET_ACCOUNTS" }));
  const response = await companyService.getAccounts(teamId, { paypointNumber }, lastIds && lastIds);
  if (response.ok()) {
    dispatch(requestSucceed({ requester: lastIds ? "GET_ACCOUNTS_PORTION" : "GET_ACCOUNTS" }));
    dispatch(requestScroll({
      requester: "GET_ACCOUNTS",
      scroll: {
        lastIds: response.data.scroll.lastIds,
        loadedAll: response.data.content.length < 50,
      }
    }));
    dispatch(lastIds ? addAccounts(response.data) : setAccounts(response.data));
  } else {
    dispatch(requestFailed({ requester: lastIds ? "GET_ACCOUNTS_PORTION" : "GET_ACCOUNTS", error: response.getError ? response.getError() : "Error" }));
  }
};

export const getAccountDetailsInvoices = (
  accountNumber: number,
  lastIds?: string | null,
  page: number = 0,
): AppThunk => async (dispatch) => {
  dispatch(requestStarted({ requester: lastIds ? "GET_INVOICES_PORTION" : "GET_INVOICES" }));
  const response = await trackingService.searchShipments({ accountNumber, lastIds, page }, true);
  if (response.isCancel?.()) return;
  if (response.ok()) {
    dispatch(requestScroll({
      requester: "GET_INVOICES",
      scroll: {
        lastIds: response.data.scroll.lastIds,
        loadedAll: response.data.content.length < 50,
        page: page + 1,
      }
    }));
    dispatch(lastIds ? addInvoices(response.data.content) : setInvoices(response.data.content));
    dispatch(requestSucceed({ requester: lastIds ? "GET_INVOICES_PORTION" : "GET_INVOICES" }));
  } else {
    dispatch(requestFailed({ requester: lastIds ? "GET_INVOICES_PORTION" : "GET_INVOICES", error: response.getError ? response.getError() : "Error"}));
  }
};

export const accountDetailsSelector = (state: IState) => state.accountDetails;

const accountDetailsReducer = accountDetailsSlice.reducer;
export default accountDetailsReducer;